/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { resendConfirmationEmail } from '@/auth/api';
import { useEmailConfirmationFormProps } from '@/auth/types';
import { useCallback, useState } from 'preact/hooks';
import { useText } from '@adobe-commerce/elsie/i18n';

export const useEmailConfirmationForm = ({
  userEmail,
  handleSetInLineAlertProps,
}: useEmailConfirmationFormProps) => {
  const translations = useText({
    emailConfirmationMessage: 'Auth.Notification.emailConfirmationMessage',
    technicalErrorSendEmail:
      'Auth.Notification.technicalErrors.technicalErrorSendEmail',
  });

  const [disabledButton, setDisabledButton] = useState(false);

  const handleEmailConfirmation = useCallback(async () => {
    setDisabledButton(true);

    if (userEmail) {
      const response = await resendConfirmationEmail(userEmail);

      if (response) {
        const errors = response?.errors?.length;
        const isEmailResend = response?.data?.resendConfirmationEmail;

        if (errors) {
          handleSetInLineAlertProps({
            type: 'error',
            text: translations.technicalErrorSendEmail,
          });
        } else {
          handleSetInLineAlertProps({
            type: isEmailResend ? 'success' : 'error',
            text: translations.emailConfirmationMessage,
          });
        }
      }
    }

    setDisabledButton(false);
  }, [handleSetInLineAlertProps, translations, userEmail]);

  return { handleEmailConfirmation, disabledButton };
};
